/* =====================================================================
   THE SKY OVER LONDON — and the sun that lives inside the portal.

   Two things, and they are the same thing.

   FIRST, the sun is real. Its bearing and its height are computed for
   51.5 N, 0.12 W from the clock in the reader's own machine, and the
   weather over that same city is asked of Open-Meteo. Nothing here is
   decoration keyed to a slider: at four in the afternoon in December the
   sun is eight degrees up in the south-west and the shading on the block
   is raking, because that is where it is.

   SECOND — and this is Tommy's ruling, 2026-09-10, after I had proposed
   the exact opposite — THE SUN BELONGS TO THE WORLD INSIDE THE PORTAL:

     "You essentially rotate the world in the portal and actually change
      the light source in the real world ... this world in the portal is
      controlling stuff and slightly more sinister than it seems."

   So the bearing computed here is a bearing OVER THE GROUND, north-up,
   and it stays attached to the ground: turn the model and the shading on
   it does not move, because you turned the country, not the sun. What
   DOES move is the room. The desk is lit THROUGH the portal, so every
   shadow on it — the sheet's on the oak, the two books', the pin's, the
   place where the wax catches — takes its direction from the sun's
   bearing PROJECTED THROUGH THE CURRENT VIEW ROTATION. At north-up the
   desk agrees with London. Turn the map a half-turn and the desk's
   shadows swing round to the other side while the block's do not, and
   that is the tell. It is meant to be consistent, not noticed.

   What this file publishes:
     · Sky.sun()    the true solar position over the ground, degrees
     · Sky.light()  the light the relief is actually shaded by: the sun,
                    or the lamp at night, or a blend toward the old fixed
                    north-west light when the sun is too low to read by
     · Sky.wash()   the sky's colours, the haze's colour and strength
     · Sky.weather() cloud, rain, fog: 0..1 and booleans
     · Sky.setView(yawDeg)  called on every view change; writes the
                    screen-space light to CSS custom properties and tells
                    anything that asked to be told
     · Sky.onChange(fn)

   `?sky=YYYY-MM-DDTHH:MM` (London wall-clock) and `?wx=clear|cloud|rain|
   fog` override both, for testing and for stills.
   ===================================================================== */
(function () {
  'use strict';

  var DEG = Math.PI / 180, RAD = 180 / Math.PI;

  /* THE PLACE. Tommy: "the weather where I live." */
  var LAT = 51.5, LON = -0.12;

  /* THE OLD LIGHT, which is still the right light when the sun cannot do
     the job. Every hachured sheet ever engraved was lit from the north-west
     at about a third of a right angle, because that is the light under
     which a relief reads; a sun three degrees above the horizon lays the
     whole country in one shadow and says nothing about its shape. So below
     this elevation the light is blended back toward the sheet's own. */
  var NW_AZ = 315, NW_EL = 34;
  var LOW_EL = 8;

  /* THE WORLD HAS ONE LIGHT AT A TIME.

     Tommy, 2026-09-10, on finding the shadows falling at one in the
     morning: "Where is the sun coming from currently when it's 1 am,
     though? Surely it's currently nighttime. What does that involve? …
     Do we want to consider the moon at night? … when it's cloudy, there
     are no shadows, and when there's little moonlight, there's less
     shadow? We can actually take this a long way, and I'm up for taking
     it a long way! Let's work out what's coherent or logical."

     The rule everything below follows from, and nothing below is added
     for decoration that does not: AT ANY MOMENT THERE IS EXACTLY ONE
     LIGHT IN THIS WORLD, and the desk is lit through the portal by it.

       THE SUN     while it is up and high enough to read a country by.
       THE MOON    while the sun is down and the moon is up: its bearing,
                   its height and its PHASE, so a full moon on a clear
                   night throws real long soft shadows and a crescent
                   barely marks the paper.
       THE LANTERN when there is neither — the survey party is still at
                   work at today's camp, and the only light in the world
                   is the one they are working by. It is a POINT in the
                   world, not a bearing: shadows radiate away from the
                   front, short beside it and long away from it.

     Between them the light is BLENDED, never doubled: two shadows on one
     desk would say there were two suns. Cloud does to all three what
     cloud does — the cast shadow shortens, softens and goes, and what is
     left under a full overcast is the contact shadow a thing has because
     it is lying on something. */

  /* THE LANTERN. Warm, low, and standing on the ground at the survey
     front. The elevation is what sets how long its shadows run; the
     bearing is worked out per object, from where the front actually is
     on the screen, so it has none of its own. */
  var LANTERN_EL = 19;

  /* How dark the world goes when the sun is down: the share of the
     printing that survives. Not nought — this is ink on paper under a
     lamp, not a photograph of a field at midnight. The moon adds a
     little of it back when it is up and full. */
  var NIGHT_LEVEL = 0.56;

  /* THE MOON IS THE ONLY CELESTIAL BODY EVER DRAWN. The sun is the
     light, not a thing: no engraver ever put the sun on a sheet with a
     face on it. The moon at night is different — it is a body you can
     see, at a bearing, with a shape — so it is drawn, small, engraved,
     at its true bearing and its true phase, and nothing else is. The
     drawing itself belongs to the wash, which is relief.js's: this file
     only says where the moon is and what shape it is in. */

  /* ================================================================
     WHERE THE SUN IS

     A low-precision solar position (the Astronomical Almanac's own
     abridged method): good to about a hundredth of a degree over this
     century, which is four decimal places more than a wash needs. No
     library — it is thirty lines.
     ================================================================ */
  function days2000(ms) { return (ms - Date.UTC(2000, 0, 1, 12, 0, 0)) / 86400000; }

  /* Right ascension and declination, in radians, turned into what a
     surveyor standing at 51.5 N actually sees: a bearing from north and a
     height above the horizon. Shared by the sun and the moon, because a
     body is a body. */
  function horizon(ra, dec, d) {
    var gmst = 18.697374558 + 24.06570982441908 * d;            // hours
    var lmst = ((gmst % 24) + 24) % 24 + LON / 15;
    var ha = (lmst * 15) * DEG - ra;
    var lat = LAT * DEG;
    var el = Math.asin(Math.sin(lat) * Math.sin(dec) +
                       Math.cos(lat) * Math.cos(dec) * Math.cos(ha));
    /* Measured from due south, west positive — which is how the classical
       formula falls out — and then turned into a bearing from north,
       clockwise, because that is what a compass rose on a sheet reads. */
    var azS = Math.atan2(Math.sin(ha),
                         Math.cos(ha) * Math.sin(lat) - Math.tan(dec) * Math.cos(lat));
    return { az: (azS * RAD + 180 + 360) % 360, el: el * RAD };
  }

  /* The sun's own ecliptic longitude, which the moon's series needs as
     well as the sky does. */
  function sunEcliptic(d) {
    var g = (357.529 + 0.98560028 * d) * DEG;                   // mean anomaly
    var q = 280.459 + 0.98564736 * d;                           // mean longitude
    var L = q + 1.915 * Math.sin(g) + 0.020 * Math.sin(2 * g);
    return { L: L, M: g * RAD, e: 23.439 - 0.00000036 * d };
  }

  function solar(ms) {
    var d = days2000(ms);
    var s = sunEcliptic(d);
    var L = s.L * DEG, e = s.e * DEG;
    var ra = Math.atan2(Math.cos(e) * Math.sin(L), Math.cos(L));
    var dec = Math.asin(Math.sin(e) * Math.sin(L));
    return horizon(ra, dec, d);
  }

  /* ================================================================
     WHERE THE MOON IS, AND WHAT SHAPE IT IS IN

     The same trade the solar routine makes, one order of magnitude
     harder: the moon's orbit is perturbed by the sun and a bare Kepler
     ellipse is two degrees out. So the classical abridged series — mean
     elements plus the twelve largest terms in longitude and five in
     latitude (evection, variation, the yearly equation and their
     company) — which lands inside a tenth of a degree. No library; it is
     forty lines, and Tommy's brief allows "a few degrees of error".

     Topocentric parallax (about a degree, because the moon is close and
     we are not at the centre of the earth) is NOT corrected: it moves the
     moon by less than its own diameter and this is a wash on a sheet, not
     an almanac.
     ================================================================ */
  function lunar(ms) {
    var d = days2000(ms) + 1.5;   // the classical series is from 1999 Dec 31.0
    function sind(x) { return Math.sin(x * DEG); }
    function cosd(x) { return Math.cos(x * DEG); }

    var N = 125.1228 - 0.0529538083 * d;    // longitude of the ascending node
    var i = 5.1454;                          // inclination
    var w = 318.0634 + 0.1643573223 * d;    // argument of perigee
    var a = 60.2666;                         // mean distance, earth radii
    var ec = 0.054900;                       // eccentricity
    var M = 115.3654 + 13.0649929509 * d;   // mean anomaly

    /* Kepler, solved the way it has always been solved: guess and correct.
       Three passes is plenty at this eccentricity. */
    var E = M + RAD * ec * sind(M) * (1 + ec * cosd(M));
    for (var k = 0; k < 3; k++) {
      E = E - (E - RAD * ec * sind(E) - M) / (1 - ec * cosd(E));
    }
    var xv = a * (cosd(E) - ec);
    var yv = a * (Math.sqrt(1 - ec * ec) * sind(E));
    var v = Math.atan2(yv, xv) * RAD;
    var r = Math.sqrt(xv * xv + yv * yv);

    /* into ecliptic coordinates */
    var xh = r * (cosd(N) * cosd(v + w) - sind(N) * sind(v + w) * cosd(i));
    var yh = r * (sind(N) * cosd(v + w) + cosd(N) * sind(v + w) * cosd(i));
    var zh = r * (sind(v + w) * sind(i));
    var lon = Math.atan2(yh, xh) * RAD;
    var lat = Math.atan2(zh, Math.sqrt(xh * xh + yh * yh)) * RAD;

    /* THE PERTURBATIONS. The sun pulls the moon about, and these are the
       terms big enough to see from a drawing board. */
    var sunE = sunEcliptic(days2000(ms));
    var Ls = sunE.L, Ms = sunE.M;
    var Lm = N + w + M;          // the moon's mean longitude
    var D = Lm - Ls;             // mean elongation
    var F = Lm - N;              // argument of latitude
    lon += -1.274 * sind(M - 2 * D) + 0.658 * sind(2 * D) - 0.186 * sind(Ms)
         - 0.059 * sind(2 * M - 2 * D) - 0.057 * sind(M - 2 * D + Ms)
         + 0.053 * sind(M + 2 * D) + 0.046 * sind(2 * D - Ms)
         + 0.041 * sind(M - Ms) - 0.035 * sind(D) - 0.031 * sind(M + Ms)
         - 0.015 * sind(2 * F - 2 * D) + 0.011 * sind(M - 4 * D);
    lat += -0.173 * sind(F - 2 * D) - 0.055 * sind(M - F - 2 * D)
         - 0.046 * sind(M + F - 2 * D) + 0.033 * sind(F + 2 * D)
         + 0.017 * sind(2 * M + F);

    /* ecliptic to equatorial, then to what is seen from London */
    var ob = sunE.e * DEG, lo = lon * DEG, la = lat * DEG;
    var xe = Math.cos(la) * Math.cos(lo);
    var ye = Math.cos(la) * Math.sin(lo) * Math.cos(ob) - Math.sin(la) * Math.sin(ob);
    var ze = Math.cos(la) * Math.sin(lo) * Math.sin(ob) + Math.sin(la) * Math.cos(ob);
    var h = horizon(Math.atan2(ye, xe), Math.asin(ze), days2000(ms));

    /* THE PHASE. The elongation from the sun IS the phase: nothing else
       is needed, and the illuminated fraction is half of one minus its
       cosine. Which limb is lit follows from whether the moon is running
       ahead of the sun (waxing) or behind it (waning). */
    var elong = ((lon - Ls) % 360 + 360) % 360;
    var illum = (1 - cosd(elong)) / 2;
    return {
      az: h.az, el: h.el, illum: illum, elong: elong,
      waxing: elong < 180,
      age: elong / 360 * 29.530589            // days since new
    };
  }

  /* What the sheet calls the shape it is drawing. Named the way an
     almanac names it, and near the full and the new — which are the only
     two a reader is ever counting from — counted in days. */
  var NUM = ['', 'one', 'two', 'three', 'four'];
  function phaseName(m) {
    var age = m.age;
    if (age < 1.0 || age > 28.5) return 'new';
    if (Math.abs(age - 14.765) < 0.9) return 'full';
    if (Math.abs(age - 7.38) < 0.9) return 'at first quarter';
    if (Math.abs(age - 22.15) < 0.9) return 'at last quarter';
    var dFull = age - 14.765;
    if (Math.abs(dFull) <= 4.4) {
      var n = Math.round(Math.abs(dFull));
      return NUM[n] + (n === 1 ? ' day ' : ' days ') + (dFull < 0 ? 'off full' : 'past full');
    }
    if (age < 7.38) return 'a waxing crescent';
    if (age < 14.765) return 'a waxing gibbous';
    if (age < 22.15) return 'a waning gibbous';
    return 'a waning crescent';
  }

  /* ================================================================
     WHAT TIME IT IS THERE

     `?sky=` is written in London wall-clock, because that is the clock
     the weather is under and the clock a reader would think in. The
     offset is asked of the platform rather than guessed from a month,
     so the last Sunday in March looks after itself.
     ================================================================ */
  function londonOffset(ms) {
    try {
      var f = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London', hour12: false,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      });
      var p = {};
      f.formatToParts(new Date(ms)).forEach(function (x) { p[x.type] = x.value; });
      var asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, (+p.hour) % 24, +p.minute);
      return (asUTC - ms) / 60000;
    } catch (e) { return 0; }
  }

  /* A LONDON WALL-CLOCK INSTANT, from the same spelling `?sky=` takes.
     One parser, so the query string and the terminal cannot disagree about
     what "2026-09-26T23:00" means. Null if it is not that. */
  function parseAt(str) {
    var m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?$/.exec(String(str || '').trim());
    if (!m) return null;
    var guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 12), +(m[5] || 0));
    return guess - londonOffset(guess) * 60000;
  }

  var SKY_Q = /[?&]sky=([^&]*)/.exec(location.search);
  var FIXED_MS = SKY_Q ? parseAt(decodeURIComponent(SKY_Q[1])) : null;

  function nowMs() { return FIXED_MS === null ? Date.now() : FIXED_MS; }

  /* ================================================================
     THE WEATHER OVER THAT CITY

     Open-Meteo: no key, no terms to accept, CORS open. Cached in the tab
     for half an hour, because the sky over London does not turn over
     faster than that and a page that refetches on every redraw is a page
     that gets itself blocked. If it fails — offline, blocked, rate-
     limited — the sky is clear and nothing is said about it. A map that
     draws an error message where the weather should be is a worse map
     than one that assumes a fine day.
     ================================================================ */
  /* ==== X5: AND THE WIND. The voyage is dead reckoning against the real
     weather over London, so the one thing the sky did not ask for is the
     one thing a boat needs. Two more fields on the SAME request — no
     second round trip, no second cache, no cost to a reader who never
     sails. `speed` is km/h; `dir` is the METEOROLOGICAL direction, the
     bearing the wind blows FROM (a westerly is 270), which is what a
     compass rose is read against. ==== */
  /* X5 asked for the wind ONLY when somebody was going to sail against
     it. X10 turned the switch round — the voyage is on unless `?voyage=0`
     — so the wind is asked for unless it is off. It is still two more
     fields on the SAME request and never a second one, and this request
     is made AFTER the first picture (R53 §4), so nothing in the cold-load
     waterfall to frame 1 moves. `?voyage=0` sends the four words this
     line has always sent, to the character. */
  var WANT_WIND = !/[?&]voyage=0(?:&|$)/.test(location.search);
  var WX_URL = 'https://api.open-meteo.com/v1/forecast?latitude=51.5&longitude=-0.12' +
               '&current=weather_code,cloud_cover,is_day,precipitation' +
               (WANT_WIND ? ',wind_speed_10m,wind_direction_10m' : '');
  var WX_KEY = 'sv-wx';
  var WX_TTL = 30 * 60 * 1000;

  var WX_Q = /[?&]wx=([^&]*)/.exec(location.search);
  var WX_FORCED = WX_Q ? decodeURIComponent(WX_Q[1]).trim().toLowerCase() : null;

  /* clear until told otherwise — and becalmed, which is the honest
     default for a boat: a wind nobody measured must not push you. */
  var WX = { cloud: 0, rain: 0, fog: false, live: false, wind: 0, windDir: 270 };

  /* ==== X5: `?wx=` CARRIES A WIND, so a voyage repeats.
       ?wx=clear|cloud|rain|fog        as before — calm, wind 0
       ?wx=clear@18                    the same sky, an 18 km/h wind from 270
       ?wx=clear@18/300                and from 300
       ?wx=@18/300                     the weather left alone, the wind pinned
     The bare four words are byte-for-byte what they were. ==== */
  function windOf(spec) {
    var m = /@\s*(\d+(?:\.\d+)?)(?:\s*\/\s*(\d+(?:\.\d+)?))?/.exec(spec || '');
    if (!m) return null;
    return { speed: +m[1], dir: m[2] === undefined ? 270 : +m[2] };
  }
  function fromForced(k) {
    var w = windOf(k), sky = String(k || '').split('@')[0].trim();
    var o = (sky === 'cloud') ? { cloud: 0.85, rain: 0, fog: false, live: false }
          : (sky === 'rain') ? { cloud: 0.92, rain: 1, fog: false, live: false }
          : (sky === 'fog') ? { cloud: 0.7, rain: 0, fog: true, live: false }
          : { cloud: 0, rain: 0, fog: false, live: false };
    o.wind = w ? w.speed : 0;
    o.windDir = w ? w.dir : 270;
    return o;
  }


  function fromApi(cur) {
    var code = +cur.weather_code;
    var wet = (code >= 51 && code <= 67) || (code >= 80 && code <= 82) ||
              (code >= 95 && code <= 99) || (code >= 71 && code <= 77) ||
              code === 85 || code === 86;
    return {
      cloud: Math.max(0, Math.min(1, (+cur.cloud_cover || 0) / 100)),
      rain: (+cur.precipitation > 0 || wet) ? Math.min(1, 0.45 + (+cur.precipitation || 0) * 0.6) : 0,
      fog: code === 45 || code === 48,
      live: true,
      /* X5: km/h, and the bearing it blows FROM */
      wind: Math.max(0, +cur.wind_speed_10m || 0),
      windDir: ((+cur.wind_direction_10m || 0) % 360 + 360) % 360
    };
  }

  /* ==== E7 (ROOM-PLAN §7): THE WEATHER IS NOT ON THE CRITICAL PATH.
     A network round trip for a TINT must not stand between the reader and
     the first picture. So the LAST ANSWER is read synchronously here — out
     of localStorage, not sessionStorage, so a repeat visit starts on the
     right sky rather than on a default — the picture is drawn with it, and
     the fetch is held until the host says the picture is up (`Sky.begin`).
     When the answer lands it is published like any other change and the
     host eases the wash to it (room/water.js readSky). ==== */
  function readCache() {
    try { return JSON.parse(localStorage.getItem(WX_KEY) || 'null'); } catch (e) { return null; }
  }
  /* however old it is: a stale sky over London beats a blank one */
  function primeWeather() {
    if (WX_FORCED) { WX = fromForced(WX_FORCED); return; }
    var c = readCache();
    if (c && c.wx) WX = c.wx;
  }
  var begun = false;
  function loadWeather() {
    begun = true;
    if (WX_FORCED) { WX = fromForced(WX_FORCED); return; }
    var cached = readCache();
    /* and a cached answer that was taken WITHOUT the wind is stale to a
       page that wants one, however fresh it is */
    if (cached && (Date.now() - cached.at) < WX_TTL && cached.wx &&
        !(WANT_WIND && cached.wx.wind === undefined)) { WX = cached.wx; return; }
    if (!window.fetch) return;
    fetch(WX_URL, { mode: 'cors' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        if (!j || !j.current) return;
        WX = fromApi(j.current);
        try { localStorage.setItem(WX_KEY, JSON.stringify({ at: Date.now(), wx: WX })); } catch (e) { }
        publish();
      })
      .catch(function () { /* clear sky, silently */ });
  }

  /* ================================================================
     COLOUR — a hand-tinted engraving, not a photograph

     Every colour below is a wash laid on this sheet's own paper. The day
     sky is NOT blue: it is the palest grey-green a tinted plate carries,
     because a saturated blue over an Ordnance palette reads instantly as
     a screen. Dawn and dusk are ochre and rose; night is indigo ink.
     Cloud pulls the lot toward a warm grey; rain darkens it.
     ================================================================ */
  var PAPER = [0xf1, 0xe7, 0xd1];

  function hex(a) {
    return '#' + a.map(function (v) {
      var s = Math.round(Math.max(0, Math.min(255, v))).toString(16);
      return s.length < 2 ? '0' + s : s;
    }).join('');
  }
  function mix(a, b, t) {
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
  }

  /* The four skies, zenith and horizon, before the weather is applied. */
  var SKIES = {
    night: { top: [0x22, 0x28, 0x3a], hor: [0x46, 0x47, 0x54] },
    dusk:  { top: [0xa8, 0x9c, 0x9a], hor: [0xe0, 0xb2, 0x8e] },
    day:   { top: [0xb2, 0xc0, 0xc1], hor: [0xe8, 0xdf, 0xc8] }
  };

  /* WHERE THE SKY IS IN ITS DAY. Below the horizon it is night; the hour
     either side of it is the warm one; above ten degrees it is simply
     day. The two crossings are eased, so nothing on the sheet ever steps. */
  function phaseMix(el) {
    if (el <= -8) return { night: 1, dusk: 0, day: 0 };
    if (el < 2) {
      var t = (el + 8) / 10;                 // -8 .. 2  night -> dusk
      return { night: 1 - t, dusk: t, day: 0 };
    }
    if (el < 12) {
      var u = (el - 2) / 10;                 // 2 .. 12  dusk -> day
      return { night: 0, dusk: 1 - u, day: u };
    }
    return { night: 0, dusk: 0, day: 1 };
  }

  function washOf(sun, wx, moon, light) {
    var p = phaseMix(sun.el);
    function band(k) {
      return [0, 1, 2].map(function (i) {
        return SKIES.night[k][i] * p.night + SKIES.dusk[k][i] * p.dusk + SKIES.day[k][i] * p.day;
      });
    }
    var top = band('top'), hor = band('hor');
    /* A MOONLIT NIGHT IS NOT A DARK NIGHT. Under a full moon on a clear
       night the sky is a pale washed indigo and you can see the ground;
       under a new moon it is ink. The lift is small — this is a tinted
       plate, and the difference between the two is the whole of what
       "some moonlight" looks like on paper. */
    if (p.night > 0 && light) {
      var lift = p.night * light.moonShare * light.lit;
      top = mix(top, [0x4a, 0x54, 0x6e], lift * 0.32);
      hor = mix(hor, [0x74, 0x79, 0x8c], lift * 0.26);
    }
    /* Cloud is a lid: it greys the wash and closes the gap between the
       zenith and the horizon, which is exactly what an overcast day does. */
    var grey = p.night > 0.5 ? [0x3a, 0x3c, 0x44] : [0xb4, 0xaf, 0xa2];
    top = mix(top, grey, wx.cloud * 0.72);
    hor = mix(hor, grey, wx.cloud * 0.55);
    top = mix(top, hor, wx.cloud * 0.35);
    if (wx.rain) { top = mix(top, [0x5e, 0x5d, 0x5a], 0.30 * wx.rain); hor = mix(hor, [0x77, 0x74, 0x6c], 0.26 * wx.rain); }

    /* THE HAZE IS THE COLOUR OF THE PAPER, TAKING THE SKY'S LIGHT.
       Tommy: "the distance wouldn't necessarily be sky ... like a blurred
       continuation ... almost like a kind of watercolour continuation."
       So the far ground does not dissolve into white and it does not
       dissolve into sky: it dissolves into the paper the sheet is printed
       on, tinted by whatever the sky is doing. Six parts paper to four
       parts horizon at noon; at night the paper goes with the room. */
    var haze = mix(PAPER, hor, 0.42 + wx.cloud * 0.14 + (wx.fog ? 0.18 : 0));
    if (p.night > 0) haze = mix(haze, mix(PAPER, hor, 0.86), p.night);
    /* RAIN DARKENS THE PAPER ITSELF, not only the sky. Without this the
       wet day and the merely grey one were the same picture at any tilt
       where the ground fills the window — which is most of them, the
       world being seven days tall and four years wide. */
    if (wx.rain) haze = mix(haze, [0x87, 0x85, 0x7e], 0.24 * wx.rain);

    return {
      top: hex(top), horizon: hex(hor), haze: hex(haze),
      hazeRgb: haze,
      /* how far the haze reaches back before it has taken everything: fog
         brings it in, a clear day pushes it out */
      reach: wx.fog ? 0.45 : (1 - wx.cloud * 0.18),
      night: p.night, dusk: p.dusk, day: p.day
    };
  }

  /* ================================================================
     THE LIGHT THE GROUND IS ACTUALLY SHADED BY
     ================================================================ */
  function smoothstep(a, b, x) {
    var t = Math.max(0, Math.min(1, (x - a) / (b - a)));
    return t * t * (3 - 2 * t);
  }
  /* Bearings are blended the SHORT way round, or a light crossing north
     swings the whole way through south to get there. */
  function blendAz(a, b, t) {
    var d = ((b - a + 540) % 360) - 180;
    return (a + d * t + 360) % 360;
  }

  /* THE ONE LIGHT.

     Three candidates and a weight each, summed to one. The blend is over
     the light itself — one bearing, one height, one strength — so there
     is never a moment with two shadows on the desk, which is the whole
     point of the rule. An hour either side of a change is about six
     degrees of the sun's height at this latitude, which is where the
     smoothsteps below come from. */
  /* THE LIGHT, FORCED. The terminal (?term=1) may pin the world to one of
     the three lights so that all three can be looked at without waiting for
     the hour to come round. Null is the rule, which is the normal state and
     the only one the site ships in. */
  var FORCED_LIGHT = null;

  function lightOf(sun, moon, wx) {
    /* THE SUN'S CLAIM. Above eight degrees it is unarguably the light; by
       the end of civil twilight, six degrees under, it has none; between
       the two it hands over. Six degrees is about forty minutes at this
       latitude in September, which is what "an hour either side" means on
       a real evening — a handover that finished ten minutes after sunset
       had the lantern lit while the western sky was still orange. */
    var wSun = smoothstep(-6, LOW_EL, sun.el);
    /* THE MOON'S CLAIM ON WHAT IS LEFT. It has to be up, and it has to be
       lit: a two-day crescent six degrees above the roofs is not what is
       lighting the room, and a lid of cloud takes it out altogether. */
    var lit = moon.illum * Math.max(0, Math.sin(moon.el * DEG));
    var wMoon = (1 - wSun) * smoothstep(0, 7, moon.el) *
                smoothstep(0.04, 0.20, lit) * (1 - smoothstep(0.55, 0.95, wx.cloud));
    var wLantern = 1 - wSun - wMoon;
    if (FORCED_LIGHT) {
      wSun = FORCED_LIGHT === 'sun' ? 1 : 0;
      wMoon = FORCED_LIGHT === 'moon' ? 1 : 0;
      wLantern = FORCED_LIGHT === 'lantern' ? 1 : 0;
      /* A forced sun or moon that is under the horizon still has to light
         something, so it is lifted to the height at which a relief reads —
         the same figure the sheet's own old light was engraved to. */
      if (wSun && sun.el < LOW_EL) sun = { az: sun.az, el: NW_EL };
      if (wMoon && moon.el < 7) moon = { az: moon.az, el: 32, illum: Math.max(0.6, moon.illum), phase: moon.phase };
      lit = moon.illum * Math.max(0, Math.sin(moon.el * DEG));
    }

    /* Each candidate as a bearing, a height, a strength and a colour. */
    var sunAz = sun.az, sunEl = sun.el;
    if (sunEl < LOW_EL) {
      /* THE OLD LIGHT still does the job the sun cannot: a sun three
         degrees up lays the whole country in one shadow and says nothing
         about its shape, so the bearing eases back toward the sheet's own
         north-west as the sun goes down. */
      var t = 1 - Math.max(0, sunEl) / LOW_EL;
      sunAz = blendAz(sunAz, NW_AZ, t);
      sunEl = sunEl + (NW_EL - sunEl) * t;
    }
    var sunWarm = Math.max(0, 1 - Math.max(0, sun.el) / 22);   // 1 at the horizon

    /* The moon is COOL, FAINT and SOFT. Its strength is the illuminated
       fraction times the sine of its height — the actual quantity of
       light reaching the ground — and a full moon riding high on a clear
       night really does throw a shadow you can read a sheet by. */
    var moonEl = Math.max(5, moon.el);
    var moonDirect = 0.14 + 0.34 * lit;

    var az = blendAz(blendAz(sunAz, moon.az, wMoon / Math.max(1e-6, wSun + wMoon)),
                     NW_AZ, wLantern);
    var el = sunEl * wSun + moonEl * wMoon + LANTERN_EL * wLantern;
    var direct = 0.55 * wSun + moonDirect * wMoon + 0.46 * wLantern;
    var level = 1 * wSun + (NIGHT_LEVEL + 0.10 * lit) * wMoon + NIGHT_LEVEL * wLantern;
    var warm = sunWarm * wSun + 0 * wMoon + 1 * wLantern;
    var cool = wMoon;

    /* HOW MUCH CAST SHADOW THERE IS AT ALL. This is the number the desk
       is really waiting for: full sun casts, a crescent barely does, and
       an overcast sky casts nothing whatever. Under full cloud what is
       left on the desk is the contact shadow, which a thing has because
       it is lying on something and not because of any light.

       THE CLOUD ONLY DIMS WHAT IS ABOVE IT. The sun and the moon are on
       the far side of the overcast and lose everything to it; the
       lantern is standing on the ground UNDER the same cloud and is not
       dimmed by it at all. Getting this wrong once — one cloud factor
       over all three — put the survey party at work at one in the
       morning under a lamp that cast no shadow. */
    var lidded = 1 - smoothstep(0.15, 0.98, wx.cloud);
    var cast = (1 * wSun + lit * wMoon) * lidded + 0.85 * wLantern;

    return {
      az: az, el: Math.max(4, el), kind: wSun > 0.5 ? 'sun' : (wMoon > wLantern ? 'moon' : 'lantern'),
      lamp: wLantern > 0.5, lantern: wLantern, moonShare: wMoon, sunShare: wSun,
      level: level, warm: warm, cool: cool, direct: direct, cast: Math.max(0, cast),
      lit: lit
    };
  }

  /* ================================================================
     PUBLISHING
     ================================================================ */
  var listeners = [];
  var viewYaw = 0;
  var cur = null;

  function state() {
    var ms = nowMs();
    var sun = solar(ms);
    var moon = lunar(ms);
    var wx = WX;
    var light = lightOf(sun, moon, wx);
    var wash = washOf(sun, wx, moon, light);
    /* Overcast flattens the shading: a directional share of a half becomes
       a quarter under a lid of cloud, which is what a grey day does to a
       relief model and to a hillside alike. */
    light.direct = light.direct * (1 - wx.cloud * 0.55);
    light.level = light.level * (1 - wx.cloud * 0.06);
    return { sun: sun, moon: moon, light: light, wash: wash, wx: wx, yaw: viewYaw };
  }

  /* THE ROOM IS LIT THROUGH THE PORTAL. The bearing that reaches the desk
     is the ground bearing turned by however far the world has been turned:
     at north-up they agree, and a half-turn of the map puts the desk's
     shadows on the other side of everything on it. */
  function screenAz(groundAz) { return (groundAz + viewYaw + 720) % 360; }

  /* WHERE THE LANTERN IS, ON THE SCREEN. sky.js knows nothing about the
     sheet and must not: the host registers a function that returns the
     survey camp's point in client coordinates, or null when the front has
     been panned out of the window. It is asked once per publish. */
  var lampSource = null;
  var lampPt = null;
  function askLamp() {
    if (!lampSource) return null;
    try { return lampSource() || null; } catch (e) { return null; }
  }
  /* The camp moves under the window on every pan, and the sheet is not
     even drawn when this module first publishes, so the point is ASKED
     FOR at the moment a shadow is struck rather than cached from a
     publish. It is one querySelector and one rect; the desk asks once per
     sweep and hands the answer to every shadow on it. */
  function lampNow() {
    var L = cur ? cur.light : null;
    if (L && L.lantern <= 0.5) return null;
    return askLamp();
  }

  /* The same numbers the CSS gets, for anything that has to strike a
     shadow in script rather than in a stylesheet — the sheet's own shadow
     on the oak and the pin's are drawn in SVG and are set from here. */
  var screenLight = { dx: 0.7071, dy: 0.7071, len: 1, soft: 1, dark: 1, az: 315, el: 34,
                      cast: 1, point: null };

  function publish() {
    var s = state();
    cur = s;
    lampPt = s.light.lantern > 0.01 ? askLamp() : null;
    var az = screenAz(s.light.az) * DEG;
    var el = Math.max(4, s.light.el);
    /* The shadow runs away from the light, in screen coordinates, where y
       is down. A bearing of nought is up the screen. */
    var dx = -Math.sin(az), dy = Math.cos(az);
    /* AND ITS LENGTH IS THE COTANGENT OF THE LIGHT'S HEIGHT — held to a
       sane range, because a sun at four degrees would otherwise throw a
       shadow fourteen times the height of the book that cast it, and
       "keep it subtle at the desk" (Tommy) rules that out. The factor is
       cut so that a light at 34 degrees gives exactly 1: that was the
       sheet's own fixed light, so at the old angle every shadow in the
       room is exactly the length it was before this iteration. */
    var len = Math.max(0.6, Math.min(1.8, 1 / Math.tan(el * DEG) * 0.674));
    var r = document.documentElement.style;
    r.setProperty('--sun-az', (screenAz(s.light.az)).toFixed(1));
    r.setProperty('--sun-el', s.light.el.toFixed(1));
    r.setProperty('--sun-dx', dx.toFixed(4));
    r.setProperty('--sun-dy', dy.toFixed(4));
    r.setProperty('--sun-len', len.toFixed(3));
    /* Cloud softens an edge and shortens what is left of it; that is the
       whole of what an overcast sky does to a shadow on a desk. */
    r.setProperty('--sun-soft', (1 + s.wx.cloud * 1.5).toFixed(3));
    r.setProperty('--sun-dark', (1 - s.wx.cloud * 0.45).toFixed(3));
    /* AND HOW MUCH CAST SHADOW THERE IS TO SOFTEN. Nought under a full
       overcast and nought under a thin crescent: every shadow in the room
       is multiplied by this, so at nought all that is left of any of them
       is the contact shadow, which is not a shadow the light casts. */
    r.setProperty('--sun-cast', s.light.cast.toFixed(3));
    r.setProperty('--night', s.wash.night.toFixed(3));
    /* LK1: how much the lamp is the light — the shaders' lampSheet()[4] */
    r.setProperty('--dim', Math.min(1, s.wash.night + 0.45 * s.wash.dusk).toFixed(3));
    /* Rain darkens the room as well as the sky: the window is grey and the
       oak goes with it. */
    r.setProperty('--wet', (s.wx.rain * (1 - s.wash.night * 0.5)).toFixed(3));
    /* where the wax on the oak catches: toward the light, off centre */
    r.setProperty('--lamp-x', (50 - dx * 22).toFixed(1) + '%');
    r.setProperty('--lamp-y', (50 - dy * 22).toFixed(1) + '%');
    r.setProperty('--sky-top', s.wash.top);
    r.setProperty('--sky-horizon', s.wash.horizon);
    r.setProperty('--sky-haze', s.wash.haze);
    r.setProperty('--sky-rain', s.wx.rain.toFixed(3));
    screenLight = {
      dx: dx, dy: dy, len: len, az: screenAz(s.light.az), el: s.light.el,
      soft: 1 + s.wx.cloud * 1.5, dark: 1 - s.wx.cloud * 0.45,
      cast: s.light.cast,
      /* THE LANTERN HAS NO BEARING OF ITS OWN. It is a point in the room,
         so every object works out its own direction from where it is
         standing relative to the light — which is what makes the shadows
         radiate away from the survey front instead of all running the
         same way. Null whenever the light is not the lantern. */
      point: s.light.lantern > 0.5 ? lampPt : null,
      lantern: s.light.lantern
    };
    s.screen = screenLight;
    listeners.forEach(function (fn) { try { fn(s); } catch (e) { } });
  }

  window.Sky = {
    state: function () { return cur || state(); },
    sun: function () { return this.state().sun; },
    light: function () { return this.state().light; },
    wash: function () { return this.state().wash; },
    weather: function () { return WX; },
    /* ==== X5: THE WIND, for anything that has to sail against it.
       { speed: km/h, dir: the bearing it blows FROM, live: whether
       anybody measured it }. Calm — speed 0 — until Open-Meteo answers or
       `?wx=clear@18/300` pins one, so a boat on a page that never reached
       the network simply has no leeway rather than an invented one. ==== */
    wind: function () {
      return { speed: WX.wind || 0, dir: WX.windDir === undefined ? 270 : WX.windDir,
               live: !!WX.live && !WX_FORCED };
    },
    /* VW1 (2026-09-27): `windText` ('WSW 18 km/h') is gone — the reader
       is told the wind in mph, in copy.json's words, by voyage.js
       (`windWords`) and day.js; this module keeps km/h */
    /* the light as it reaches the room: a unit direction for a shadow on
       the desk, how long it is, how soft, how dark */
    screen: function () { return screenLight; },
    /* Called by the relief on every view change: the only input this
       module has that is not the clock or the weather. */
    setView: function (yawDeg) {
      var y = ((yawDeg || 0) % 360 + 360) % 360;
      if (Math.abs(y - viewYaw) < 0.05) return;
      viewYaw = y;
      publish();
    },
    yaw: function () { return viewYaw; },
    moon: function () { return this.state().moon; },
    /* The host tells this module where the survey camp is drawn, in client
       coordinates, and nothing else about the sheet ever reaches here. */
    setLampSource: function (fn) { lampSource = fn; publish(); },
    /* where the lantern is on the screen RIGHT NOW, or null if the light
       is not the lantern */
    lampPoint: lampNow,
    onChange: function (fn) { listeners.push(fn); if (cur) fn(cur); },
    fixed: FIXED_MS !== null,
    /* the light note (`note`, `noteText`) went on 09-20: "we killed all the
       other text, why would we keep this?" */
    phaseName: function () { return phaseName(this.state().moon); },

    /* ---- THE OVERRIDES, PUBLISHED (v22, section E) -------------------
       `?sky=` and `?wx=` were parsed once at load and never touched again.
       The terminal needs the same two dials at runtime, and one more: which
       of the three lights is on. They are the same values the query string
       sets, set by the same code paths, so there is exactly one way the
       world's clock and weather can be moved. */
    setTime: function (ms) {
      FIXED_MS = (ms === null || ms === undefined) ? null : +ms;
      publish();
      return FIXED_MS;
    },
    /* The instant the world is at, whether that is the clock or an
       override, and how it reads on a London wall clock. */
    at: function () { return nowMs(); },
    override: function () { return FIXED_MS; },
    atText: function () {
      try {
        return new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Europe/London', year: 'numeric', month: 'short',
          day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false
        }).format(new Date(nowMs()));
      } catch (e) { return new Date(nowMs()).toISOString().slice(0, 16); }
    },
    /* clear | cloud | rain | fog, or null for whatever London is doing */
    setWeather: function (kind) {
      WX_FORCED = kind || null;
      if (WX_FORCED) { WX = fromForced(WX_FORCED); publish(); }
      else { try { localStorage.removeItem(WX_KEY); } catch (e) { } WX = fromForced('clear'); loadWeather(); publish(); }
      return WX_FORCED;
    },
    forcedWeather: function () { return WX_FORCED; },
    /* sun | moon | lantern, or null for the rule */
    setLight: function (kind) {
      FORCED_LIGHT = (kind === 'sun' || kind === 'moon' || kind === 'lantern') ? kind : null;
      publish();
      return FORCED_LIGHT;
    },
    forcedLight: function () { return FORCED_LIGHT; },
    parseAt: parseAt
  };

  /* ==== E7: the cache now, the network after the first picture ==== */
  window.Sky.begin = function () { if (!begun) loadWeather(); };
  primeWeather();
  /* and if nobody ever says so (`?plate=old`, or a host that does not
     know about this), ask anyway, well clear of the first paint */
  setTimeout(function () { window.Sky.begin(); }, 1500);
  publish();
  /* The sun moves. A quarter of a degree of arc takes a minute, and
     nothing here animates, so once a minute is generous. */
  /* Unconditional now: an override can be cleared at runtime (the terminal),
     and a world whose clock had been switched off at load would then have
     stopped for good. Publishing a fixed state costs nothing. */
  setInterval(publish, 60000);
  setInterval(loadWeather, WX_TTL);
})();
